/**
 * "Who's in your flat" — the age/gender row on the public room page (COL-530).
 *
 * One chip per person currently living in the other rooms of this apartment,
 * carrying their age and, as the chip's fill, their gender. Deliberately not a
 * list of people: there are no names, no avatars and no ordering by anything
 * that identifies someone. `get_room_flatmates` hands this component two
 * scalars per person and nothing else, so there is nothing here to leak.
 *
 * Either field can be missing, and both often are — gender only exists for
 * tenants who applied after COL-530 shipped or whom an admin has filled in,
 * and date of birth is collected after signup rather than at application, so
 * a signed tenant can easily have neither. Product decision (Neel,
 * 2026-10-08): "show what is available, if none are available then just an
 * empty dot" — a person with nothing known still occupies a chip, because the
 * row doubles as the flat's headcount and hiding them would under-report it.
 */
import { INK, LINE_STRONG, MUTED_SOFT, SAND } from "@/lib/marketing-theme";

export type Flatmate = {
  age: number | null;
  gender: "female" | "male" | null;
};

/**
 * The chips overlap as one stack, the way the design draws them. Each needs a
 * ring in the colour of what sits behind it to stay a separate head rather
 * than a blob: the facts strip is on the room page's `bg-white`, so the ring
 * is white. A chip on any other surface would need this changed with it.
 */
const PAGE = "#FFFFFF";
/** How far each chip slides under the one before it. */
const OVERLAP = -8;

/** The two values `profiles.gender` can hold, and how each chip reads. */
const FILL = {
  female: { background: INK, color: SAND, border: INK },
  male: { background: SAND, color: INK, border: LINE_STRONG },
  unknown: { background: "transparent", color: MUTED_SOFT, border: LINE_STRONG },
} as const;

function label(gender: Flatmate["gender"], de: boolean): string {
  if (gender === "female") return de ? "Frau" : "Woman";
  if (gender === "male") return de ? "Mann" : "Man";
  return de ? "Keine Angabe" : "Not specified";
}

/** "27, Frau" / "Mann" / "Keine Angabe" — what a screen reader reads out. */
function describe(mate: Flatmate, de: boolean): string {
  const who = label(mate.gender, de);
  if (mate.age == null) return who;
  return de ? `${mate.age} Jahre, ${who}` : `${mate.age} years old, ${who}`;
}

export function FlatmateChips({
  flatmates,
  de,
}: {
  flatmates: Flatmate[];
  de: boolean;
}) {
  if (flatmates.length === 0) return null;

  // The legend only names the genders actually standing in this flat — a
  // two-entry key under a row of four women reads as a question about who the
  // missing men are.
  const present = (["female", "male"] as const).filter((g) =>
    flatmates.some((m) => m.gender === g)
  );

  return (
    <div className="flex flex-col gap-2">
      <ul className="flex flex-wrap items-center" role="list">
        {flatmates.map((mate, i) => {
          const fill = FILL[mate.gender ?? "unknown"];
          return (
            <li
              key={i}
              className="relative flex h-8 w-8 items-center justify-center rounded-full text-[13px] font-semibold tabular-nums"
              style={{
                backgroundColor: fill.background,
                color: fill.color,
                border: `1px solid ${fill.border}`,
                letterSpacing: "-0.01em",
                // Earlier chips sit on top, so the stack reads left to right.
                marginLeft: i === 0 ? 0 : OVERLAP,
                zIndex: flatmates.length - i,
                boxShadow: `0 0 0 2px ${PAGE}`,
              }}
              title={describe(mate, de)}
            >
              <span className="sr-only">{describe(mate, de)}</span>
              <span aria-hidden="true">{mate.age ?? ""}</span>
            </li>
          );
        })}
      </ul>

      {present.length > 0 && (
        <div
          className="flex flex-wrap items-center gap-3 text-[12px] font-medium"
          style={{ color: MUTED_SOFT }}
          aria-hidden="true"
        >
          {present.map((g) => (
            <span key={g} className="flex items-center gap-1.5">
              <span
                className="h-2 w-2 rounded-full"
                style={{
                  backgroundColor: FILL[g].background,
                  border: `1px solid ${FILL[g].border}`,
                }}
              />
              {label(g, de)}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
